/**
 * Possible status codes indicating the evaluation result of a creative's rendering.
 */
export type Code = 'BEGIN_TO_RENDER' | 'ERROR' | 'NO_CREATIVE';

/**
 * Represents the response structure for evaluating whether a creative has begun to render.
 */
export interface CreativeBeginToRenderResponse {
    /** Indicates whether the creative has begun to render successfully. */
    success: boolean;

    /** The status code indicating the result of the evaluation. */
    code: Code;
}

/**
 * Evaluates whether a creative inside the provided container has begun to render.
 * This is determined by checking if any visible images within the container have loaded.
 *
 * @param creativeContainer - The HTML element containing the creative.
 * @returns A response indicating whether rendering has begun.
 */
export const evaluateCreativeBeginToRender = (creativeContainer: HTMLElement): CreativeBeginToRenderResponse => {
    const images = Array.from(creativeContainer.querySelectorAll('img')).filter(
        (x) => getComputedStyle(x).display !== 'none',
    );
    return evaluateAnyImageLoaded(images);
};

/**
 * Evaluates whether any image in the provided list has successfully loaded.
 *
 * The function checks if at least one image is fully loaded (i.e., it is marked as complete
 * and has non-zero dimensions). If no images are present, it returns `NO_CREATIVE`.
 * If at least one image loads successfully, it returns `BEGIN_TO_RENDER`.
 * If all images fail to load, it returns `ERROR`.
 *
 * @param images - An array of HTML image elements to evaluate.
 * @returns A response indicating whether rendering has begun, no creative was found, or an error occurred.
 */
const evaluateAnyImageLoaded = (images: HTMLImageElement[]): CreativeBeginToRenderResponse => {
    if (images.length === 0) {
        return { success: false, code: 'NO_CREATIVE' };
    }

    return images.some((img) => img.complete && img.naturalWidth > 0 && img.naturalHeight > 0)
        ? { success: true, code: 'BEGIN_TO_RENDER' }
        : { success: false, code: 'ERROR' };
};
